import React, { useEffect, useState } from "react";
import apiClient from "../../services/apiClient";

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
}

interface Provider {
  id: number;
  user_id: number;
  additional_info?: { qualification?: string };
  user: { first_name: string; last_name: string };
}

interface ServiceProvider {
  id: number;
  user_id: number;
  additional_info?: { GSTIN?: string };
  user: { first_name: string; last_name: string };
}

interface ProvidersResponse {
  code: number;
  message: string;
  data: { providers: Provider[] };
}

interface ServiceProvidersResponse {
  code: number;
  message: string;
  data: { service_providers: ServiceProvider[] };
}

export default function AddRecommendedProviderModal({ onClose, onSuccess }: Props) {
  const [formData, setFormData] = useState({
    role: "",
    selected_id: "",
    user_id: "",
  });

  const [providers, setProviders] = useState<Provider[]>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const [provRes, spRes] = await Promise.all([
        apiClient<ProvidersResponse>("/v1/resource/providers/all?isActiveUsers=1", { method: "GET" }),
        apiClient<ServiceProvidersResponse>("/v1/resource/service_providers/all?isActiveUsers=1", { method: "GET" }),
      ]);
      setProviders(provRes.data?.providers || []);
      setServiceProviders(spRes.data?.service_providers || []);
    } catch (error) {
      console.error("Fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleAddRecommended = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.role || !formData.selected_id)
      return alert("Please select role and name");

    try {
      const payload =
        formData.role === "provider"
          ? { provider_id: Number(formData.selected_id), user_id: Number(formData.user_id) }
          : { service_provider_id: Number(formData.selected_id), user_id: Number(formData.user_id) };

      await apiClient("/v1/resource/recommendedProviders", {
        method: "POST",
        body: payload,
      });

      alert("Recommended added successfully!");
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Add failed:", error);
      alert("Failed to add recommended provider.");
    }
  };

  const nameOptions: (Provider | ServiceProvider)[] =
    formData.role === "provider" ? providers : serviceProviders;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200">
      <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-2xl w-[480px] max-h-[90vh] overflow-y-auto border border-gray-200/50 animate-in slide-in-from-bottom-4 duration-300">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Add Recommended
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-full"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleAddRecommended} className="space-y-5">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">Select Role</label>
            <select
              value={formData.role}
              onChange={(e) =>
                setFormData({ role: e.target.value, selected_id: "", user_id: "" })
              }
              className="w-full border-2 border-gray-200 rounded-xl p-3 bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all outline-none hover:border-gray-300"
              required
            >
              <option value="">-- Select Role --</option>
              <option value="provider">Provider</option>
              <option value="service_provider">Service Provider</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">Select Name</label>
            {loading ? (
              <div className="flex items-center justify-center p-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <select
                value={formData.selected_id}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedItem = nameOptions.find((n: Provider | ServiceProvider) => n.id === Number(selectedId));
                  setFormData({
                    ...formData,
                    selected_id: selectedId,
                    user_id: selectedItem ? String(selectedItem.user_id) : "",
                  });
                }}
                className="w-full border-2 border-gray-200 rounded-xl p-3 bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all outline-none hover:border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={!formData.role || nameOptions.length === 0}
                required
              >
                <option value="">
                  {!formData.role ? "Select role first" : "-- Select Name --"}
                </option>
                {nameOptions.map((n: Provider | ServiceProvider) => (
                  <option key={n.id} value={n.id}>
                    {n.user.first_name} {n.user.last_name}
                    {/* {formData.role === "provider"
                      ? ` • ${(n as Provider).additional_info?.qualification || "N/A"}`
                      : ` • GSTIN: ${(n as ServiceProvider).additional_info?.GSTIN || "N/A"}`} */}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all hover:scale-105 active:scale-95"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all hover:scale-105 hover:shadow-xl hover:shadow-blue-500/40 active:scale-95"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}